import React, { useEffect, useState } from 'react';
import platformService from '../api/platformService';

const formatCurrency = (amount) => `Rp ${Number(amount || 0).toLocaleString('id-ID')}`;

const STATUS_LABELS = {
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Past Due',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  trialing: 'bg-blue-50 text-blue-700',
  active: 'bg-green-50 text-green-700',
  past_due: 'bg-yellow-50 text-yellow-700',
  suspended: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const OverviewPage = () => {
  const [data, setData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    platformService.getOverview()
      .then((res) => {
        if (res.status === 'success') {
          setData(res.data);
        } else {
          setError('Failed to load overview.');
        }
      })
      .catch(() => setError('Failed to load overview.'));

    platformService.getAuditLogs({ limit: 10 })
      .then((res) => {
        if (res.status === 'success') setAuditLogs(res.data);
      })
      .catch(() => {});
  }, []);

  if (error) {
    return <div className="text-red-600">{error}</div>;
  }

  if (!data) {
    return <div className="text-gray-500">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Platform Overview</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="text-sm text-gray-500">Total Tenants</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{data.total_tenants}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Estimated MRR</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{formatCurrency(data.mrr_estimate)}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Active + Trialing</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">
            {(data.tenant_counts.active || 0) + (data.tenant_counts.trialing || 0)}
          </div>
        </div>
      </div>

      <div className="card p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Tenants by Status</h2>
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.tenant_counts).map(([status, count]) => (
            <span key={status} className={`badge ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABELS[status] || status}: {count}
            </span>
          ))}
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recently Registered</h2>
        {data.recent_tenants.length === 0 ? (
          <p className="text-gray-500 text-sm">No tenants yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="pb-2">Subdomain</th>
                <th className="pb-2">Name</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Registered</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_tenants.map((t) => (
                <tr key={t.subdomain} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 font-medium text-gray-900">{t.subdomain}</td>
                  <td className="py-2 text-gray-600">{t.name}</td>
                  <td className="py-2">
                    <span className={`badge ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-700'}`}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </td>
                  <td className="py-2 text-gray-500">{t.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-6 mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Platform Activity</h2>
        {auditLogs.length === 0 ? (
          <p className="text-gray-500 text-sm">No activity recorded yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {auditLogs.map((log) => (
              <li key={log._id} className="flex justify-between border-b border-gray-100 last:border-0 pb-2">
                <span className="text-gray-700">
                  <span className="font-medium">{log.actor_email}</span> — {log.action.replace('_', ' ')}
                </span>
                <span className="text-gray-400">{log.created_at}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default OverviewPage;
